import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Boxes,
  Check,
  ChefHat,
  Combine,
  Package,
  Printer,
  Table2,
  Tags,
  Tag,
} from 'lucide-react';

/**
 * Sprint 62 — Primeros pasos: el orden para armar el menú.
 *
 * Existe por un reporte que no era un bug: *"ahora que el sistema está vacío estoy
 * PERDIDO, ¿quién me dice el orden en que debo crear las cosas?"*.
 *
 * Y tenía razón: el orden y las dependencias sólo vivían en el código. El asistente
 * de instalación cubre negocio, logo, impuestos y dueño; del menú, nada. Cada pantalla
 * del catálogo se podía llenar en cualquier orden, y el orden equivocado deja el
 * trabajo a medias sin avisar.
 *
 * **Sprint 82 — el orden cambió, y no por gusto.** La guía empezaba por Inventario porque un
 * producto tenía que engancharse a un ingrediente **ya existente**. Con el alta nueva es al revés:
 * dar de alta "Lomo de Cerdo · kg · tengo 10 · merma 40%" **crea** su ingrediente y sus cuatro
 * tamaños de una vez. Seguir el orden viejo ahora produce un choque —"ya tienes Lomo de Cerdo en
 * tu inventario"— en el primer producto que se capture.
 *
 * El inventario dejó de ser un paso previo y pasó a ser lo que siempre fue en el negocio: **el día
 * a día**, donde se repone lo que llega y se ve lo que queda.
 *
 * Dos decisiones que sostienen la pantalla:
 *
 *  1. **Se marca sola.** Cada paso lee el estado real y se pone en verde con el conteo.
 *     El operador no interpreta nada, lo ve. Es el mismo patrón de la pantalla del
 *     agente de impresión, el único instructivo que el cliente logró seguir sin ayuda.
 *  2. **Dice qué es opcional.** Saber que puede saltarse categorías y paquetes es la
 *     diferencia entre capturar veinte productos hoy o abandonar en el tercero.
 */

interface Paso {
  numero: number;
  titulo: string;
  /** Para qué sirve, en una frase, sin vocabulario de sistema. */
  para: string;
  /** Qué pasa si se salta. `null` = es necesario. */
  siLoSaltas: string | null;
  ruta: string;
  icono: typeof Package;
  hechos: number;
  /** Sustantivo para el conteo: "3 ingredientes". */
  unidad: string;
}

export function PrimerosPasosPage() {
  const [conteos, setConteos] = useState({
    insumos: 0,
    categorias: 0,
    productos: 0,
    sinControl: 0,
    paquetes: 0,
    promociones: 0,
    mesas: 0,
    impresora: 0,
  });
  const [cargando, setCargando] = useState(true);

  const cargar = useCallback(async () => {
    const [insumos, categorias, productos, paquetes, promos, mesas, impresion] = await Promise.all([
      window.api.insumos.listar(),
      window.api.categorias.listar(),
      window.api.productos.listar(),
      window.api.paquetes.listar(),
      window.api.promociones.listar(),
      window.api.mesas.listar(),
      window.api.impresion.estado(),
    ]);
    setConteos({
      insumos: insumos.ok ? insumos.data.filter((i) => i.activo).length : 0,
      categorias: categorias.ok ? categorias.data.length : 0,
      productos: productos.ok ? productos.data.length : 0,
      // Sprint 63 (Fase 3) — Decisión del negocio: TODOS los productos del menú deben
      // descontar y avisar. Un producto sin ingrediente ligado deja de ser una opción
      // cómoda y pasa a ser un pendiente visible.
      sinControl: productos.ok
        ? productos.data.filter((p) => p.stockEfectivo === null || p.stockEfectivo === undefined)
            .length
        : 0,
      paquetes: paquetes.ok ? paquetes.data.length : 0,
      promociones: promos.ok ? promos.data.length : 0,
      mesas: mesas.ok ? mesas.data.length : 0,
      impresora: impresion.ok ? impresion.data.agentes.filter((a) => a.conectado).length : 0,
    });
    setCargando(false);
  }, []);

  useEffect(() => {
    void cargar();
    // Sondeo lento: la pantalla puede quedar abierta en otra pestaña mientras se
    // captura, y tiene que ponerse en verde sin que nadie la recargue.
    const id = window.setInterval(() => void cargar(), 5_000);
    return () => window.clearInterval(id);
  }, [cargar]);

  const pasos: Paso[] = [
    {
      numero: 1,
      titulo: 'Categorías',
      para: 'Los grupos de botones del punto de venta: Carnes, Bebidas, Postres.',
      siLoSaltas:
        'Opcional, pero hacerlas primero ahorra trabajo: al dar de alta cada producto ya puedes elegir su grupo, en vez de reclasificar cuarenta después.',
      ruta: '/categorias',
      icono: Tags,
      hechos: conteos.categorias,
      unidad: 'categorías',
    },
    {
      numero: 2,
      titulo: 'Productos',
      para: 'Lo que vendes, con su precio. En un solo paso dices qué es, cómo lo compras (kilos, litros, piezas), cuánto tienes hoy con su merma, y en qué tamaños lo vendes: 1 kg, 3/4, 1/2, 1/4. El sistema arma el inventario solo.',
      siLoSaltas: null,
      ruta: '/productos',
      icono: Package,
      hechos: conteos.productos,
      unidad: 'productos',
    },
    {
      numero: 3,
      titulo: 'Paquetes',
      para: 'Combinaciones con precio propio: una espada con papas y refresco.',
      siLoSaltas: 'Opcional. Necesitan que los productos ya estén capturados.',
      ruta: '/paquetes',
      icono: Combine,
      hechos: conteos.paquetes,
      unidad: 'paquetes',
    },
    {
      numero: 4,
      titulo: 'Promociones',
      para: 'Descuentos y regalos: 2x1, gratis con cierta compra, precio especial por horas.',
      siLoSaltas: 'Opcional. Van al final porque se arman sobre productos y paquetes que ya existen.',
      ruta: '/promos',
      icono: Tag,
      hechos: conteos.promociones,
      unidad: 'promociones',
    },
    {
      numero: 5,
      titulo: 'Mesas',
      para: 'Para atender en el local. Cada mesa lleva su cuenta abierta.',
      siLoSaltas: 'Sólo si vendes nada más para llevar o a domicilio.',
      ruta: '/mesas',
      icono: Table2,
      hechos: conteos.mesas,
      unidad: 'mesas',
    },
    {
      numero: 6,
      titulo: 'Impresora de tickets',
      para: 'Conecta la caja para que los tickets y las comandas salgan solos.',
      siLoSaltas: 'Opcional. Sin esto se puede cobrar igual, pero sin ticket impreso.',
      ruta: '/settings',
      icono: Printer,
      hechos: conteos.impresora,
      unidad: 'cajas conectadas',
    },
  ];

  const necesarios = pasos.filter((p) => p.siLoSaltas === null);
  const listoParaVender = necesarios.every((p) => p.hechos > 0) && conteos.productos > 0;

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-bold">Primeros pasos</h1>
        <p className="mt-1 text-slate-600">
          Este es el orden para armar tu menú. Cada paso se marca en verde solo, en cuanto lo
          completas: no tienes que acordarte de nada.
        </p>
      </div>

      {!cargando &&
        (listoParaVender ? (
          <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
            <div className="flex items-center gap-2 font-semibold text-emerald-800">
              <ChefHat className="h-5 w-5" />
              Ya puedes vender
            </div>
            <p className="mt-1 text-sm text-emerald-800">
              Tienes {conteos.productos} productos capturados. Lo que sigue es abrir el punto de
              venta y probar una venta de prueba.
            </p>
            <Link
              to="/pos"
              className="btn-primary mt-3 inline-flex min-h-[44px] items-center gap-2"
            >
              Ir al punto de venta <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4">
            {/*
              Sprint 82 — Este consejo decía exactamente lo contrario ("empieza por los
              ingredientes"), y con el alta nueva llevaba a un choque en el primer producto.
            */}
            <p className="text-sm text-slate-700">
              <strong>Empieza por tus productos.</strong> En un solo paso dices qué vendes, cómo lo
              compras, cuánto tienes hoy y en qué tamaños lo vendes — el inventario se arma solo. No
              hace falta capturar nada antes.
            </p>
          </div>
        ))}

      {/*
        Sprint 82 — El inventario dejó de ser un paso previo.
        Ya no se captura para poder vender: se usa todos los días para reponer lo que llega y ver
        lo que queda. Ponerlo aquí abajo, separado y sin número, es lo que evita que alguien crea
        que tiene que llenarlo antes de empezar.
      */}
      {!cargando && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 font-semibold text-slate-700">
            <Boxes className="h-5 w-5" />
            Después, el día a día
          </div>
          <p className="mt-1 text-sm text-slate-600">
            <strong>Existencias</strong> ya no se captura al principio: se llena solo con lo que das
            de alta. Entra ahí para <strong>agregar lo que llega</strong> —cuando te surten más
            carne o refrescos—, ver cuánto queda y registrar mermas.
            {conteos.insumos > 0 && ` Hoy llevas cuenta de ${conteos.insumos} cosas.`}
          </p>
          <Link
            to="/inventario"
            className="mt-3 inline-flex min-h-[44px] items-center gap-2 text-sm font-medium text-primary"
          >
            Ir a Existencias <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      {/*
        Sprint 63 (Fase 3) — Lo que quedó a medias, a la vista.
        Un producto sin ingrediente se vende igual (Sprint 62) pero no lleva cuenta de
        existencias. Si se cuela por descuido, el negocio se queda sin avisos justo en
        lo que más rota — y no hay ninguna pantalla donde eso se note.
      */}
      {!cargando && conteos.sinControl > 0 && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
          <p className="text-sm font-semibold text-amber-900">
            {conteos.sinControl === 1
              ? 'Hay 1 producto que no descuenta inventario'
              : `Hay ${conteos.sinControl} productos que no descuentan inventario`}
          </p>
          <p className="mt-1 text-sm text-amber-800">
            Se pueden vender sin problema, pero el sistema no va a avisarte cuando se acaben. En
            Productos, arriba, tienes el aviso para unirlos con lo que ya tienes en inventario.
          </p>
          <Link
            to="/productos"
            className="mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-amber-600 px-4 text-sm font-medium text-white hover:bg-amber-700"
          >
            Revisar productos <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}

      <ol className="space-y-3">
        {pasos.map((paso) => {
          const hecho = paso.hechos > 0;
          const Icono = paso.icono;
          return (
            <li
              key={paso.numero}
              className={`card flex items-start gap-4 ${hecho ? 'border-emerald-200 bg-emerald-50/40' : ''}`}
            >
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold ${
                  hecho ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-600'
                }`}
                aria-hidden
              >
                {hecho ? <Check className="h-6 w-6" /> : paso.numero}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-base font-semibold text-slate-800">
                    <Icono className="mr-1 inline h-4 w-4 text-slate-400" />
                    {paso.titulo}
                  </h2>
                  {hecho ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800">
                      {paso.hechos} {paso.unidad}
                    </span>
                  ) : paso.siLoSaltas === null ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                      Necesario
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">
                      Opcional
                    </span>
                  )}
                </div>

                <p className="mt-1 text-sm text-slate-600">{paso.para}</p>
                {!hecho && paso.siLoSaltas && (
                  <p className="mt-1 text-xs text-slate-500">{paso.siLoSaltas}</p>
                )}

                <Link
                  to={paso.ruta}
                  className={`mt-3 inline-flex min-h-[44px] items-center gap-2 rounded-lg px-4 text-sm font-medium ${
                    hecho
                      ? 'text-slate-600 hover:bg-slate-100'
                      : 'bg-primary text-white hover:opacity-90'
                  }`}
                >
                  {hecho ? `Ver ${paso.titulo.toLowerCase()}` : `Capturar ${paso.titulo.toLowerCase()}`}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </li>
          );
        })}
      </ol>

      <p className="pb-4 text-xs text-slate-500">
        Puedes volver a esta pantalla cuando quieras desde el menú lateral. Los pasos opcionales no
        estorban: si no los necesitas, déjalos así.
      </p>
    </div>
  );
}
